import {useEffect, useState} from 'react'

import {groupBy} from 'es-toolkit'
import {isEmpty} from 'es-toolkit/compat'
import invariant from 'invariant'
import {Route, Routes, useLocation, useMatch, useNavigate, useParams} from 'react-router'

import config from '../../config'

import {useAppDispatch, useAppSelector} from '../../hooks/redux'
import {signOut} from '../../redux/features/auth'
import {
  openAdditionalCostModal,
  openRequestRefundModal,
  openSimpleConfirmModal,
} from '../../redux/features/modal'
import {setOrder} from '../../redux/features/order'

import useGuard from '../../../../commons/src/hooks/use-guard'
import {HttpStatusError} from '../../../../commons/src/libs/http-json'
import {exceedsMaxDaysRange} from '../../../../commons/src/services/date'
import useOnBeforeUnload from '../../hooks/use-on-before-unload'
import {
  countUnreadMessages,
  hasUnreadMessages,
  isValidForAdditionalCost,
  sanitizeHistory,
} from '../../libs/order'
import {getOrder} from '../../services/api/partner'
import * as partnerApi from '../../services/api/partner'
import {captureException, captureIgnoredException} from '../../services/logging'
import {goToOrderDetail, goToOrders} from '../../services/navigation'

import {ProviderReview} from '../../../../commons/src/types'

import bankNoteIcon from '../../../../commons/src/assets/icons/bank-note.svg'
import chevronLeftIcon from '../../../../commons/src/assets/icons/chevron-left.svg'
import coinsHandIcon from '../../../../commons/src/assets/icons/coins-hand.svg'

import ActionBar from '../../../../commons/src/components/action-bar/ActionBar'
import Button from '../../../../commons/src/components/button'
import CounterTag from '../../../../commons/src/components/counter-tag'
import Headline from '../../../../commons/src/components/headline'
import Icon from '../../../../commons/src/components/icon'
import List from '../../../../commons/src/components/list'
import Paragraph from '../../../../commons/src/components/paragraph'
import Stack from '../../../../commons/src/components/stack'
import StarRating from '../../../../commons/src/components/star-rating'
import Tabs from '../../../../commons/src/components/tabs'
import Tab from '../../../../commons/src/components/tabs/Tab'
import Tooltip from '../../../../commons/src/components/tooltip'
import TooltipHandler from '../../../../commons/src/components/tooltip-handler'
import Container from '../../components/container/Container'
import ContentLoader from '../../components/content-loader/ContentLoader'
import PageContentLayout from '../../components/page-content-layout'
import PageHeader from '../../components/page-content-layout/PageHeader'

import {Order, OrderDetailTab, OrderStatusType} from '../../order-types'
import ROUTES from '../../routes'
import PagePartial from '../page-partial'

import OrderDetailsPartial from './order-details-partial'
import OrderDetailsSidebarPartial from './order-details-sidebar-partial'
import OrderMessagesPartial from './order-messages-partial'
import OrderReviewPartial from './order-review-partial'
import OrderStatusPartial from './order-status-partial'

const OrderDetailPage = () => {
  const dispatch = useAppDispatch()
  const provider = useAppSelector(state => state.core.provider)

  const [orderState, setOrderState] = useState<Order | undefined>()
  const [orderReview, setOrderReview] = useState<ProviderReview>()
  const [isLoadingOrderReview, setIsLoadingOrderReview] = useState(true)
  const [askForReviewEnabled, setAskForReviewEnabled] = useState(false)
  const [isLoadingOrder, setIsLoadingOrder] = useState(true)
  const [loadingOrderFailed, setLoadingOrderFailed] = useState(false)
  const [overlayOpen, setOverlayOpen] = useState(false)

  const params = useParams()
  const orderId = params.id

  const navigate = useNavigate()
  const location = useLocation()

  const fetchOrder = async () => {
    setIsLoadingOrder(true)
    setLoadingOrderFailed(false)
    try {
      const fetchedOrder = await partnerApi.getOrder(orderId as string)
      setOrderState(fetchedOrder)
    } catch (error) {
      if (error && error instanceof HttpStatusError && error.response.status === 401) {
        captureIgnoredException(error)
        dispatch(signOut())
      } else {
        captureException(error)
        setLoadingOrderFailed(true)
      }
    } finally {
      setIsLoadingOrder(false)
    }
  }

  useEffect(() => {
    if (location.state?.order) {
      setOrderState(location.state.order)
      setIsLoadingOrder(false)
    } else if (orderId) {
      fetchOrder()
    } else {
      goToOrders(navigate)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const getAskForReviewEnabled = () => {
    invariant(orderState, 'Missing order')
    const hasReceivedStatus = orderState.printingService.status === OrderStatusType.RECEIVED
    const orderNotOlderThanHalfAYear = !exceedsMaxDaysRange(
      orderState.created,
      new Date(),
      config.askForReview.maxDaysOld
    )
    const receivedHistoryItem = groupBy(sanitizeHistory(orderState), state => state.status)[
      OrderStatusType.RECEIVED
    ]
    const receivedForAtLeastAWeek = receivedHistoryItem
      ? exceedsMaxDaysRange(receivedHistoryItem[0].date, new Date(), config.askForReview.minDaysOld)
      : false
    return hasReceivedStatus && orderNotOlderThanHalfAYear && receivedForAtLeastAWeek
  }

  useEffect(() => {
    if (orderState) {
      if (orderState.hasReview) {
        partnerApi
          .getOrderReview(orderState.orderId)
          .then(review => {
            setOrderReview(review)
            setAskForReviewEnabled(false)
          })
          .catch(error => {
            if (error && error instanceof HttpStatusError && error.response.status === 401) {
              captureIgnoredException(error)
              signOut()
            } else {
              captureException(error, 'default', {
                originalError: error,
                orderId,
              })
            }
          })
          .finally(() => setIsLoadingOrderReview(false))
      } else {
        // no review available yet - check if provider can ask for a review
        setOrderReview(undefined)
        setAskForReviewEnabled(getAskForReviewEnabled())
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderState])

  const match = useMatch<'tab', string>({
    path: `${ROUTES.ORDER_DETAIL.replace(':id', orderId as string)}/:tab`,
  })

  const activeTab = match?.params.tab || OrderDetailTab.DETAILS

  const isLoading = isLoadingOrder

  const resetLocationState = () => {
    window.history.replaceState({}, '')
  }

  useOnBeforeUnload(resetLocationState)

  if (
    useGuard(isLoading || (typeof orderId === 'string' && !isEmpty(orderState)), () =>
      goToOrders(navigate)
    )
  ) {
    return null
  }

  const hasMessages = orderState && orderState.messages && orderState.messages.length > 0

  const requestAdditionalCostIsValid =
    orderState && hasMessages && isValidForAdditionalCost(orderState)
  const requestRefundIsValid = hasMessages

  const onOrderUpdate = (updatedOrder: Order) => {
    setOrderState(updatedOrder)
    dispatch(setOrder({order: updatedOrder}))
  }

  const onOrderReviewUpdate = (updatedReview: ProviderReview) => {
    setOrderReview(updatedReview)
  }

  const renderAdditionalCostButton = (enabled: boolean) => (
    <Button
      hierarchy="secondary"
      icon={<Icon source={bankNoteIcon} />}
      onClick={() => {
        if (orderState && provider) {
          dispatch(
            openAdditionalCostModal({
              order: orderState,
              providerId: provider?.id,
              onDone: async () => {
                // refetch the order and update state to show newly added additional costs
                const updatedOrder = await getOrder(orderState.orderId)
                setOrderState(updatedOrder)
                dispatch(setOrder({order: updatedOrder}))
              },
            })
          )
        }
      }}
      disabled={!enabled}
    >
      Request additional costs
    </Button>
  )

  const renderAdditionalCostTooltipButton = () => (
    <TooltipHandler
      tooltip={
        <Tooltip appearance="dark">
          {hasMessages ? (
            <>
              <span>
                You cannot request additional cost via the partner portal because the order was
                either already shipped, received by the customer or cancelled.
              </span>
              <br />
              <br />
              <span>
                If you need to request additional cost anyways, please contact the{' '}
                <strong>Craftcloud team</strong> to help you out.
              </span>
            </>
          ) : (
            'Please contact the customer first before requesting additional cost.'
          )}
        </Tooltip>
      }
    >
      {renderAdditionalCostButton(false)}
    </TooltipHandler>
  )

  const renderRequestRefundButton = (enabled: boolean) => (
    <Button
      hierarchy="secondary"
      icon={<Icon source={coinsHandIcon} />}
      onClick={() => {
        if (orderState && provider) {
          dispatch(
            openSimpleConfirmModal({
              title: 'Are you sure you want to refund?',
              content: (
                <Stack>
                  <Paragraph className="u-no-margin-bottom">
                    Please be mindful about refunds. Customers are more likely to go elsewhere if
                    they have a bad experience with their order.
                  </Paragraph>
                  <Paragraph strong className="u-no-margin-bottom">
                    Please consider the following aspects before refunding:
                  </Paragraph>
                  <List>
                    <li>Can the order be saved by charging additional costs?</li>
                    <li>Can the customer revise their model? Or can you revise their model?</li>
                    <li>Have you offered the customer alternative materials or options?</li>
                  </List>
                </Stack>
              ),
              onConfirm: () => {
                dispatch(
                  openRequestRefundModal({
                    order: orderState,
                    providerId: provider.id,
                    onConfirm: async () => {
                      // refetch the order and update state to show newly added refund requests
                      const updatedOrder = await getOrder(orderState.orderId)
                      setOrderState(updatedOrder)
                      dispatch(setOrder({order: updatedOrder}))
                    },
                  })
                )
              },
              confirmButtonLabel: 'Confirm & continue',
            })
          )
        }
      }}
      disabled={!enabled}
    >
      Request refund
    </Button>
  )

  const renderRequestRefundTooltipButton = () => (
    <TooltipHandler
      tooltip={
        <Tooltip appearance="dark">
          Please contact the customer first before requesting a refund.
        </Tooltip>
      }
    >
      {renderRequestRefundButton(false)}
    </TooltipHandler>
  )

  return (
    <PagePartial fixedHeight>
      <PageContentLayout
        fixedHeight
        loader={<ContentLoader loading={isLoading} />}
        overlay={
          !!orderState && (
            <OrderDetailsSidebarPartial order={orderState} onClose={() => setOverlayOpen(false)} />
          )
        }
        isOverlayOpen={overlayOpen}
        onOverlayClose={() => {
          setOverlayOpen(false)
        }}
        header={
          <PageHeader
            headline={
              <ActionBar>
                <Button
                  hierarchy="secondary"
                  icon={<Icon source={chevronLeftIcon} />}
                  onClick={() => goToOrders(navigate)}
                />
                <Headline
                  size="l"
                  label={`Order ${!isLoading && !!orderState ? `#${orderState.orderNumber}` : ''}`}
                  addendum={
                    orderState?.appId && provider?.apps && provider?.apps.length > 1
                      ? ` (${orderState.appId})`
                      : ''
                  }
                  className="u-no-margin-bottom"
                />
                {!isLoading && !!orderState && (
                  <OrderStatusPartial
                    order={orderState}
                    onOrderUpdate={(updatedOrder: Order) => {
                      setOrderState(updatedOrder)
                      dispatch(setOrder({order: updatedOrder}))
                    }}
                  />
                )}
                {orderState?.printingService.status !== OrderStatusType.UNKNOWN &&
                orderState?.printingService.status !== OrderStatusType.CANCELLED
                  ? requestAdditionalCostIsValid
                    ? renderAdditionalCostButton(true)
                    : renderAdditionalCostTooltipButton()
                  : null}
                {!!orderState && !!orderReview && (
                  <StarRating
                    stars={orderReview.total}
                    suffix={`${orderReview.total} out of 5`}
                    onClick={event => {
                      event.preventDefault()
                      goToOrderDetail(navigate, {
                        orderId: orderState.orderId,
                        activeTab: OrderDetailTab.REVIEW,
                        order: orderState,
                        replace: true,
                      })
                    }}
                  />
                )}
              </ActionBar>
            }
            actions={
              config.refund.enabled &&
              orderState?.printingService.status !== OrderStatusType.UNKNOWN &&
              orderState?.printingService.status !== OrderStatusType.CANCELLED
                ? requestRefundIsValid
                  ? renderRequestRefundButton(true)
                  : renderRequestRefundTooltipButton()
                : null
            }
          >
            <Tabs
              activeTab={activeTab}
              onChange={tabKey =>
                !!orderState &&
                goToOrderDetail(navigate, {
                  orderId: orderState.orderId,
                  activeTab: tabKey as OrderDetailTab,
                  order: orderState,
                  replace: true,
                })
              }
            >
              <Tab label="Details" tabKey={OrderDetailTab.DETAILS} disabled={isLoading} />
              <Tab
                label={
                  <>
                    Messages{' '}
                    {!!orderState && hasUnreadMessages(orderState) && (
                      <CounterTag appearance="alternative">
                        {countUnreadMessages(orderState)}
                      </CounterTag>
                    )}
                  </>
                }
                tabKey={OrderDetailTab.MESSAGES}
                disabled={isLoading}
              />
              {orderReview && (
                <Tab label="Review" tabKey={OrderDetailTab.REVIEW} disabled={isLoading} />
              )}
            </Tabs>
          </PageHeader>
        }
      >
        {!isLoading && loadingOrderFailed && (
          <Container>
            <Paragraph error>
              We are currently unable to load this order. Please wait a few minutes and then try
              again.
            </Paragraph>
            <Button hierarchy="secondary" onClick={() => fetchOrder()}>
              Try again
            </Button>
          </Container>
        )}
        {!isLoading && !loadingOrderFailed && !!orderState && (
          <Routes>
            <Route
              path={OrderDetailTab.MESSAGES}
              element={
                <OrderMessagesPartial
                  order={orderState}
                  openOverlay={() => setOverlayOpen(true)}
                  onOrderUpdate={onOrderUpdate}
                  hasReview={!!orderReview}
                  askForReviewEnabled={askForReviewEnabled}
                />
              }
            />
            <Route
              path={OrderDetailTab.REVIEW}
              element={
                <OrderReviewPartial
                  order={orderState}
                  orderReview={orderReview}
                  isLoading={isLoadingOrderReview}
                  onOrderReviewUpdate={onOrderReviewUpdate}
                />
              }
            />
            <Route
              path={OrderDetailTab.DETAILS}
              element={<OrderDetailsPartial order={orderState} onOrderUpdate={onOrderUpdate} />}
            />
            <Route
              path="/"
              element={<OrderDetailsPartial order={orderState} onOrderUpdate={onOrderUpdate} />}
            />
          </Routes>
        )}
      </PageContentLayout>
    </PagePartial>
  )
}

export default OrderDetailPage
